import './index.css';
import React from 'react'

import { Summary as SummaryComponent } from './Summary'
import { Chat as ChatComponent } from './Chat'

import { useMutation, useQuery, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'

import { audioToText, createSummaryAction, getSummaryAction } from './actions/ai'
import createPrompt from './components/_utils/createPrompt';
import createSteps from './components/_utils/createSteps';
import clearConversation from './components/_utils/clearConversation';
import generateQuickMessages from './components/_utils/generateQuickMessages';
import { getDatasetAction } from './actions/web';
import toggleFavorites from './components/_utils/toggleFavorites';
import { onDownloadPDF } from './components/_utils/onDownloadPdf';

const queryClient = new QueryClient()

interface ChatbotContextProps {
	filename: string;
	dataset_id: string;
	project_id: string;
	quickMessages: string[];
	loading: boolean;
	handleQuickMessages: (prompt: string) => void;
	setQuickMessages: React.Dispatch<React.SetStateAction<any[]>>;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const ChatbotContext = React.createContext<ChatbotContextProps>({
	filename: '',
	dataset_id: '',
	project_id: '',
	quickMessages: [],
	loading: false,
	handleQuickMessages: () => {},
	setQuickMessages: () => {},
	setLoading: () => {}
})


export type ChatbotProps = {
	filename: string
	customStyles?: string
	dataset_id: string;
	project_id: string;
	createQuickMessagesUrl?: string
	children: React.ReactNode
}

function Chatbot(props: ChatbotProps){
	const filename = props.filename
	const dataset_id = props.dataset_id
	const project_id = props.project_id
	const createQuickMessagesUrl = props.createQuickMessagesUrl
	const [quickMessages, setQuickMessages] = React.useState<string[]>([])
	const [usedQuickMessages, setUsedQuickMessages] = React.useState<string[]>([])
	const [loading, setLoading] = React.useState(false)
	const isMounted = React.useRef(false)

	const customStyles = props.customStyles
	interface CustomStyles {
		colors: { [key: string]: string };
		classes: { [key: string]: string };
	}

	async function readConfigFile(filePath: string): Promise<CustomStyles | null> {
		try {
		  const response = await fetch(filePath);
		  if (!response.ok) {
			throw new Error(`${response.statusText}`);
		  }
		  
		  return await response.json();
		} catch (error) {
		  console.error('Error reading JSON config file:', error);
		  return null;
		}
	}

	function updateCSS(customStyles: CustomStyles) {
		if (!customStyles) return;
		
		for (const className in customStyles.classes) {
			const colorCode = customStyles.colors[className];
			document.documentElement.style.setProperty(`${className}`, colorCode);
		}
	}

	React.useEffect(() => {
		if(customStyles){
			readConfigFile(customStyles)
			.then(customStyles => {
				if(customStyles) {
					updateCSS(customStyles)
				}
			})
		}
	},[customStyles])

	const handleQuickMessages = React.useCallback(
		async (prompt: string) => {
		  await generateQuickMessages({filename, createQuickMessagesUrl, prompt, quickMessages, usedQuickMessages, setLoading, setQuickMessages, setUsedQuickMessages });
		}, [filename, quickMessages, usedQuickMessages, createQuickMessagesUrl, setLoading, setQuickMessages]
	);

	React.useEffect(() => {
		if(isMounted.current){
			if (filename) {
				setQuickMessages([])
				handleQuickMessages('')
			}
		}else{
			isMounted.current = true
		}
	}, [filename, createQuickMessagesUrl]);

    return (
        <ChatbotContext.Provider value={{filename, dataset_id, project_id, quickMessages, loading, handleQuickMessages, setQuickMessages, setLoading}}>
            {props.children}
        </ChatbotContext.Provider>
    )
}

function useChatbotContext(){
    const context = React.useContext(ChatbotContext)
    if(!context){
        throw new Error(
            `Chatbot compound components cannot be rendered outside the Chatbot component`,
          )
    }
    return context
}

export type ChatProps = {
	createDatasetPromptUrl?: string
	createDatasetStepsUrl?: string
	clearConversationUrl?: string
	audioToTextUrl?: string
}

//to-do: make sure to clear everything at start of conversation
function ChatHandler(props: ChatProps){
	const createDatasetPromptUrl = props.createDatasetPromptUrl
	const createDatasetStepsUrl = props.createDatasetStepsUrl
	const clearConversationUrl = props.clearConversationUrl
	const audioToTextUrl = props.audioToTextUrl
	
	const { filename } = useChatbotContext()
	const { loading, setLoading } = useChatbotContext()
	const { quickMessages, setQuickMessages } = useChatbotContext()
	const { handleQuickMessages } = useChatbotContext()
	
	const [messages, setMessages] = React.useState<any[]>([])
	const [favoriteMessages, setFavoriteMessages] = React.useState<any[]>([])
	const [showSteps, setshowSteps] = React.useState(false)

	const [input, setInput] = React.useState('')
	const [context, setContext] = React.useState('')
	const [latestMessageIndex, setLatestMessageIndex] = React.useState(-1);
	const [contextOpen, setContextOpen] = React.useState(false);
  
	const [modalOpen, setModalOpen] = React.useState(false);
	const [largeImageUrl, setLargeImageUrl] = React.useState('')
	
	const getDatasetQuery = useQuery({
		queryKey: ['dataset'],
		queryFn: async () => {
			try {
				return await getDatasetAction(filename)
			} catch (err) {
				if (err instanceof AxiosError) {
					toast.error(err.response?.data.message ?? err.message)
				}
				return Promise.reject(err)
			}
		}
	})

	const handleCreatePrompt = React.useCallback(
		async (prompt: string, context: string) => {
			createPrompt({filename, createDatasetPromptUrl, prompt, context, messages, setMessages, setLoading, setshowSteps});
		}, [filename, messages]
	);

	const handleCreateSteps = React.useCallback(
    async () => {
      createSteps({filename, createDatasetStepsUrl, messages, setMessages, setLoading, setshowSteps});
    }, [filename, messages]
  	);

	const handleClearConversation = React.useCallback(
    async (showToast = true) => {
      clearConversation({filename, clearConversationUrl, showToast, setshowSteps});
    }, [filename]
  );

	React.useEffect(() => {
		if (filename) {
			setMessages([]) 
			handleClearConversation(false)
		}
	}, [filename]);

	const handleFavorites = React.useCallback((prompt: string, isFavorite: boolean) => {
			toggleFavorites({prompt, isFavorite, favoriteMessages, setFavoriteMessages});
		}, [favoriteMessages]
	);

	const handleContext = async () => {
		setContextOpen(!contextOpen)
	}

	const handleOnDownloadPdf = () => {
		onDownloadPDF(messages, filename)
	}

	const onSendMessage = async (message: string) => {
		setInput('')
		handleCreatePrompt(message, context)
	}

	// final usable favorites list
	const favoritesList = React.useMemo(() => [...favoriteMessages], [favoriteMessages])

	const chatContainerRef = React.useRef<HTMLDivElement>(null)

	React.useEffect(() => {
		if (chatContainerRef.current) {
			chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
		}
		setLatestMessageIndex(messages.length - 1);
	}, [messages])

	const inputRef = React.useRef<HTMLInputElement>(null)
	const contextRef = React.useRef<HTMLInputElement>(null)

	React.useEffect(() => {
		if (inputRef.current && messages.length % 2 === 0) {
			inputRef.current.focus()
		}
	}, [messages.length])

	const handleOpenModal = (imageUrl: string) => {
		setLargeImageUrl(imageUrl);
		setModalOpen(!modalOpen);
	}

	const mimeType = "audio/webm"
	const mediaRecorder = React.useRef<any>(null)
	const [recordingStatus, setRecordingStatus] = React.useState<any>(false)
	const [audioChunks, setAudioChunks] = React.useState<any>([])

	const startRecording = async () => {
		setRecordingStatus(true)

		if ("MediaRecorder" in window) {
			try {
				const streamData = await navigator.mediaDevices.getUserMedia({
					audio: true,
					video: false,
				});

				const media = new MediaRecorder(streamData, { mimeType })
				mediaRecorder.current = media
				mediaRecorder.current.start()
				let localAudioChunks: any[] = []

				mediaRecorder.current.ondataavailable = (event: { data: any }) => {
					if (typeof event.data === "undefined") return
					if (event.data.size === 0) return 
					localAudioChunks.push(event.data)
				}
				setAudioChunks(localAudioChunks)
			} catch (error) {
				if (error instanceof AxiosError) {
					toast.error(error.response?.data.message ?? error.message)
				}
				return Promise.reject(error)
			}
		} else {
			alert("The MediaRecorder API is not supported in your browser.");
		}
	}

	const stopRecording = () => {
		setRecordingStatus(false)

		mediaRecorder.current.stop()
		
		mediaRecorder.current.onstop = async () => {
			const audioBlob = new Blob(audioChunks, { type: mimeType })

			let formData = new FormData()
			formData.append('audio_file', audioBlob, 'recordedAudio.webm')

			const formattedDataset = filename.replace(/ /g, '_')
			try {
				const result = await audioToText(formattedDataset, audioBlob, audioToTextUrl)
				setInput(result)
			} catch (err) {
				if (err instanceof AxiosError) {
					toast.error(err.response?.data.message ?? err.message)
				}
				return Promise.reject(err)
			} 
		}
	}

	return <ChatComponent
		filename={filename}
		loading={loading}
		messages={messages}
		latestMessageIndex={latestMessageIndex}
		quickMessages={quickMessages}
		favoriteMessages={favoriteMessages}
		showSteps={showSteps}
		input={input}
		inputRef={inputRef}
		context={context}
		contextOpen={contextOpen}
		contextRef={contextRef}
		chatContainerRef={chatContainerRef}
		recordingStatus={recordingStatus}
		setLoading={setLoading}
		setContext={setContext}
		setInput={setInput}
		setQuickMessages={setQuickMessages}
		handleCreatePrompt={handleCreatePrompt}
		handleCreateSteps={handleCreateSteps}
		handleClearConversation={handleClearConversation}
		handleQuickMessages={handleQuickMessages}
		handleFavorites={handleFavorites}
		handleContext={handleContext}
		handleOnDownloadPdf={handleOnDownloadPdf}
		onSendMessage={onSendMessage}
		handleOpenModal={handleOpenModal}
		startRecording={startRecording}
		stopRecording={stopRecording}
	/>
}

function Chat(props: ChatProps) {
	return(
		<QueryClientProvider client={queryClient}>
			<ChatHandler {...props}/>
		</QueryClientProvider>
	)
}

export type SummaryProps = {
	getSummaryUrl?: string
	createSummaryUrl?: string
}

function SummaryHandler(props: SummaryProps) {
	const { filename } = useChatbotContext()
	const { dataset_id } = useChatbotContext()
	const { project_id } = useChatbotContext()
	const { quickMessages } = useChatbotContext()
	const { loading } = useChatbotContext()

	const getSummaryUrl = props.getSummaryUrl
	const createSummaryUrl = props.createSummaryUrl

    const formattedDataset = filename.replace(/ /g, '_')

	const getSummaryQuery = useQuery({
		queryKey: ['summary', formattedDataset, dataset_id, project_id],
		queryFn: async () => {
			try {
				return await getSummaryAction(formattedDataset, getSummaryUrl)
			} catch (err) {
				if (err instanceof AxiosError) {
					alert(err.response?.data.message ?? err.message)
				}
				return Promise.reject(err)
			}
		}
	})

	const createSummaryMutation = useMutation({
		mutationFn: async () => {
			try {
				return await createSummaryAction(formattedDataset, createSummaryUrl)
			} catch (err) {
				if (err instanceof AxiosError) {
					alert(err.response?.data.message ?? err.message)
				}
				return Promise.reject(err)
			}
		},
		onSuccess: () => {
			getSummaryQuery.refetch()
		}
	})

	React.useEffect(() => {
		if (
			!getSummaryQuery.data?.summary &&
			!getSummaryQuery.isLoading &&
			!createSummaryMutation.isLoading
		) {
			createSummaryMutation.mutate()
		}
	}, [
		createSummaryMutation,
		getSummaryQuery.data?.summary,
		getSummaryQuery.isLoading
	])

    return(
		<SummaryComponent 
			getSummaryQuery={getSummaryQuery} 
			createSummaryMutation={createSummaryMutation} 
			quickMessages={quickMessages}
			loading={loading}
		/>
	)
}

function Summary(props: SummaryProps){
	return(
		<QueryClientProvider client={queryClient}>
			<SummaryHandler {...props}/>
		</QueryClientProvider>
	)
}

Chatbot.Summary = Summary
Chatbot.Chat = Chat

export default Chatbot